import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { Table } from '../../common/tanstacktable';
import { cleanArray } from '../../common/utils';
import translations from '../translations/DimensionViewTable.translations.json';
import {
  DataObjectDimensionType,
  DataObjectOutputType,
  DataObjectView,
  DimensionValidFormDataType,
} from '../types';
import { DimensionClassifierValue } from './DimensionClassifierValue';
import { classifierFilterFn, classifierSortingFn } from './utils';
import { Flex } from 'antd';

const { addTranslations, useTranslation } = i18nNsFactory(
  'dimension-view-table',
);
addTranslations(translations);

export interface DimensionViewTableProps {
  dataObjectView: DataObjectView;
  outputTypeCode?: DataObjectOutputType;
  dimensions: DimensionValidFormDataType[];
}

const columnHelper = createColumnHelper<DimensionValidFormDataType>();

export function DimensionViewTable({
  dataObjectView,
  outputTypeCode,
  dimensions,
}: DimensionViewTableProps) {
  const { t } = useTranslation();
  const columns = useMemo(
    () =>
      cleanArray([
        columnHelper.accessor('dimensionType.name', {
          header: t('dimensionType.label'),
          filterFn: 'includesOption',
          meta: {
            className: 'sticky-left',
            width: '20ch',
          },
        }),
        columnHelper.accessor('classifier', {
          header: t('column.classifier'),
          cell: (props: CellContext<DimensionValidFormDataType, any>) => (
            <DimensionClassifierValue {...props.row.original.classifier} />
          ),
          filterFn: classifierFilterFn,
          meta: {
            width: '30%',
            minWidth: 180,
          },
          sortingFn: classifierSortingFn,
        }),
        columnHelper.accessor('code', {
          header: t('column.code'),
          meta: {
            width: '30ch',
          },
        }),
        columnHelper.accessor('name', {
          header: t('column.name'),
          meta: {
            width: '40%',
            minWidth: 150,
          },
        }),
        columnHelper.accessor('description', {
          header: t('column.description'),
          meta: {
            width: '30%',
            minWidth: 150,
          },
        }),
        columnHelper.accessor('width', {
          header: t('column.width'),
          meta: {
            width: '30%',
            minWidth: 150,
            textAlign: 'right',
          },
        }),
        dataObjectView === DataObjectView.OUT &&
          columnHelper.accessor('constantValue', {
            header: t('column.constant'),
            meta: {
              width: 150,
            },
          }),
        columnHelper.accessor('required', {
          header: t('column.required'),
          cell: memo<CellContext<DimensionValidFormDataType, any>>((props) => {
            const required = props.row.original.required;
            return required !== null ? (
              <Flex
                justify="center"
                id={`required-checkbox-${props.row.original.id}`}
              >
                {t(required ? 'yes' : 'no')}
              </Flex>
            ) : null;
          }),
          meta: { width: 90 },
          enableColumnFilter: false,
          enableSorting: false,
        }),
        dataObjectView === DataObjectView.OUT &&
          outputTypeCode !== DataObjectOutputType.REPORT &&
          columnHelper.accessor('aggregateHierarchy', {
            header: t('column.aggregateHierarchy'),
            cell: memo<CellContext<DimensionValidFormDataType, any>>(
              (props) => {
                const aggregateHierarchy =
                  props.row.original.aggregateHierarchy;
                return aggregateHierarchy !== null ? (
                  <Flex
                    justify="center"
                    id={`aggregateHierarchy-checkbox-${props.row.original.id}`}
                  >
                    {t(aggregateHierarchy ? 'yes' : 'no')}
                  </Flex>
                ) : null;
              },
            ),
            meta: { width: 90 },
            enableColumnFilter: false,
            enableSorting: false,
          }),
      ]),
    [dataObjectView, outputTypeCode, t],
  );

  const filterOptions = useMemo(
    () => ({
      dimensionType_name: Object.values(DataObjectDimensionType).map((code) => {
        const label = t(`dimensionType.${code}`);
        return {
          id: label,
          name: label,
        };
      }),
    }),
    [t],
  );

  return (
    <TableWrapper vertical align="end" gap="middle">
      <Table
        name="dimension-view-table"
        data={dimensions || []}
        options={filterOptions}
        columns={columns}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </TableWrapper>
  );
}

export const TableWrapper = styled(Flex)`
  margin: auto;
`;
